import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdApi, type PdOpportunity, type PdScoringQuestion } from '../api';
import '../screens.css';
import './ScoringForm.css';

/**
 * The scoring Q&A form.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/opportunities-score-form.ejs`.
 *
 * OPENING THIS SCREEN HAS SIDE EFFECTS, which is unusual for a read and is the source's
 * design: it moves the opportunity to "Scoring in Progress" and LOCKS its framework. The
 * lock is the important half — once anyone has begun scoring against a framework, editing
 * its questions would invalidate the answers already given.
 *
 * SAVE versus SUBMIT are genuinely different operations:
 *   * Save stores the answers and nothing else. Partial is fine; come back later.
 *   * Submit refuses a partial set, then computes the weighted score, resolves the
 *     recommendation band, writes the per-dimension breakdown and marks the opportunity
 *     Scored. All of it in one transaction.
 *
 * Answers are grouped by dimension then component for display only — the order the server
 * returns them in is the framework's own display order, and it is preserved as given.
 */

export function ScoringForm({ opportunityId }: { opportunityId: string }) {
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState<PdOpportunity | null>(null);
  const [questions, setQuestions] = useState<PdScoringQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  /* The locked shell's height. Measured, not `100%`: the platform shell never bounds its
     content area. A CALLBACK ref, not `useRef` — this screen mounts behind a loading gate,
     so an effect keyed on a ref would run once against null and never apply a height. */
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.scoring.form(opportunityId);
      setOpportunity(data.opportunity);
      setQuestions(data.questions);
      const existing: Record<string, number> = {};
      for (const q of data.questions) {
        if (q.selected_answer_id !== null) existing[String(q.question.id)] = q.selected_answer_id;
      }
      setAnswers(existing);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Grouped for rendering only; the server's order is the framework's display order.
  const groups = useMemo(() => {
    const out: {
      dimension: PdScoringQuestion['dimension'];
      components: { component: PdScoringQuestion['component']; questions: PdScoringQuestion[] }[];
    }[] = [];
    for (const q of questions) {
      let d = out.find((x) => x.dimension.id === q.dimension.id);
      if (!d) {
        d = { dimension: q.dimension, components: [] };
        out.push(d);
      }
      let c = d.components.find((x) => x.component.id === q.component.id);
      if (!c) {
        c = { component: q.component, questions: [] };
        d.components.push(c);
      }
      c.questions.push(q);
    }
    return out;
  }, [questions]);

  const answered = Object.keys(answers).length;
  const total = questions.length;

  const save = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await pdApi.scoring.save(opportunityId, answers);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await pdApi.scoring.submit(opportunityId, answers);

      /*
       * A SUBMIT DOES NOT ALWAYS PRODUCE A SCORE.
       *
       * The questionnaire is split between up to three owners, and the overall score waits
       * for the LAST question across every aspect. So a submit has two outcomes: this
       * person's sections are in and the score is now computed, or they are in and somebody
       * else still owes answers.
       *
       * Navigating to Completed on the second outcome would be a lie — nothing was scored,
       * and the opportunity is not on that screen. So it stays here and says who is being
       * waited on, which is the difference between "did my submit work?" and "whose answers
       * are we missing?".
       */
      if (r.pending) {
        const waiting = r.waitingOn?.length ? r.waitingOn.join(' and ') : 'other owners';
        setNotice(
          `Your sections are complete and saved. ${r.answered ?? 0} of ${r.total ?? 0} questions ` +
            `answered across the whole questionnaire — waiting on ${waiting} before the score is calculated.`,
        );
        return;
      }

      navigate('/portfolio_definition/opportunities/completed', { replace: true });
      // Kept for the case where navigation is blocked — the result should not vanish.
      setNotice(
        `Scored ${Number(r.overall_score).toFixed(2)}${r.recommendation ? ` — ${r.recommendation}` : ''}`,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  if (loading) return <div className="pd-screen pd-loading">Loading…</div>;

  return (
    /*
     * THE HEADING AND BUTTONS DO NOT SCROLL — owner-directed, and CLAUDE.md's standing rule
     * that a screen header is never scrollable and only the data region moves. The height is
     * MEASURED rather than set to 100%, because this shell never bounds its content area, so
     * a percentage resolves against nothing and the window scrolls instead.
     */
    <div
      className="pd-screen sf-page pd-screen--locked"
      ref={setRootEl}
      style={rootH === null ? undefined : { height: `${rootH}px` }}
    >
      <div className="sf-fixed">
        <div className="pd-page-header">
          <div>
            <h1 className="pd-page-header__title">
              Score Opportunity {opportunity?.opportunity_id}
            </h1>
            <p className="pd-page-header__subtitle">{opportunity?.description}</p>
          </div>
          <div className="pd-page-header__actions">
            {/* For an owner this is THEIR questions — the server sends only their sections,
                so the count is their own workload rather than the whole questionnaire's. */}
            <span className="pd-count">
              {answered} of {total} answered
            </span>
            <button type="button" className="pd-btn" disabled={busy} onClick={save}>
              Save progress
            </button>
            <button
              type="button"
              className="pd-btn pd-btn--primary"
              disabled={busy || answered < total}
              title={
                answered < total
                  ? `Answer all ${total} questions before submitting — ${total - answered} remaining`
                  : undefined
              }
              onClick={submit}
            >
              Submit score
            </button>
          </div>
        </div>

        {error ? <div className="pd-error">{error}</div> : null}

        {/*
         * The "Progress saved." confirmation was removed at the owner's request — *"remove
         * that section stating Progress Saved. No need for that."* The save still happens
         * and still reports failure; only the success banner is gone, because the answered
         * count in the header already moves and says the same thing.
         *
         * `notice` is kept for the one case that is NOT a routine save: the scored result,
         * which is set when navigation away is blocked and must not vanish silently.
         */}
        {notice ? <div className="pd-notice">{notice}</div> : null}
      </div>

      <div className="sf-scroll">
        {groups.map((g) => (
        <section key={g.dimension.id} className="sf-dimension">
          <h2 className="sf-dimension-title">{g.dimension.name}</h2>
          {g.dimension.description ? (
            <p className="sf-dimension-desc">{g.dimension.description}</p>
          ) : null}

          {g.components.map((c) => (
            <div key={c.component.id} className="sf-component">
              <div className="sf-component-head">
                <span className="sf-component-name">{c.component.name}</span>
                {/* Weight drives the score: an answer here counts weight/5 as much as a
                    maximum answer on a weight-5 component. Shown so that is not hidden. */}
                <span className="sf-weight" title="Component weight, 1–5">
                  weight {c.component.weightage}
                </span>
              </div>

              {c.questions.map((q) => (
                <div key={q.question.id} className="sf-question">
                  <div className="sf-question-text">{q.question.text}</div>
                  {q.question.description ? (
                    <div className="sf-question-desc">{q.question.description}</div>
                  ) : null}
                  <div className="sf-answers">
                    {q.answers.map((a) => {
                      const selected = answers[String(q.question.id)] === a.id;
                      return (
                        <label
                          key={a.id}
                          className={`sf-answer${selected ? ' sf-answer--on' : ''}`}
                        >
                          <input
                            type="radio"
                            name={`q${q.question.id}`}
                            checked={selected}
                            onChange={() =>
                              setAnswers({ ...answers, [String(q.question.id)]: a.id })
                            }
                          />
                          {/*
                           * THE SCORE IS NOT SHOWN TO THE PERSON ANSWERING. Owner-directed,
                           * 2026-08-10: *"you need to remove scores from questions once a
                           * client [is] answering questions. They should not see the
                           * scores."*
                           *
                           * A visible 1–5 beside each choice tells the respondent which
                           * answer scores best, which is an invitation to answer for the
                           * score rather than for the truth — and the whole assessment is
                           * only worth what its honesty is worth.
                           *
                           * The value is still SENT and still stored; only the display is
                           * gone. The scores remain visible where they belong — the
                           * Framework Builder, the completed score, and the audit trail.
                           */}
                          <span className="sf-answer-text">{a.choice_text}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </section>
        ))}
      </div>
    </div>
  );
}
